import { FC, lazy, LazyExoticComponent, ReactNode, Suspense } from "react"

import {
  ActionFunction,
  LazyRouteFunction,
  LoaderFunction,
  RouterProvider,
  createBrowserRouter,
  useRoutes,
  type RouteObject,
} from "react-router"

import { useAuth0 } from "@auth0/auth0-react"

import { clusterAPI } from "@/utils/api"
import { s } from "@/utils/i18n"

import { Button } from "@/components/Button"
import Link from "@/components/Link"

import AuthLayout from "./AuthLayout"
import HeaderBar from "./HeaderBar"
import ModalLayout from "./ModalLayout"
// eslint-disable-next-line import/no-cycle
import NavLayout from "./NavLayout"
import NotFoundPage from "./NotFound"
import PublicLayout from "./PublicLayout"
import RootLayout, { GlobalLoader } from "./RootLayout"

const importComponent = (
  _import: () => Promise<
    { loader?: LoaderFunction; ErrorBoundary?: FC<any> } & Record<string, any> &
      ({ component: FC<any> } | { default: FC<any> })
  >
): LazyRouteFunction<any> => {
  return async () => {
    const module = await _import()
    const { ErrorBoundary, default: _default, ...members } = module
    const Component = members.Component || _default || Object.values(members).find((x) => typeof x === "function")

    const comp: any = { Component, ErrorBoundary }
    Object.entries(members).forEach(([key, value]) => {
      comp[key] = value
    })

    return comp
  }
}

type RouteWithHeader = Omit<RouteObject, "children"> & {
  header?: ReactNode
  headerLazy?: LazyExoticComponent<any>
  children?: RouteWithHeader[]
}

const passFormDataAction: ActionFunction<FormData> = async ({ request }) => {
  const formData = await request.formData()
  return formData
}

const SignOutButton = () => {
  const { logout } = useAuth0()

  return <Button as="button" size="small" type="button" onClick={() => logout()}>{s`Sign Out`}</Button>
}

export const routes: RouteWithHeader[] = [
  {
    path: "",
    element: (
      <Suspense fallback={<GlobalLoader />}>
        <RootLayout />
      </Suspense>
    ),
    errorElement: <NotFoundPage />,
    hydrateFallbackElement: <GlobalLoader />,
    children: [
      // (public)
      {
        path: "",
        element: <PublicLayout />,
        errorElement: <NotFoundPage />,
        children: [
          {
            path: "/",
            lazy: importComponent(() => import("./Login")),
          },
        ],
      },
      // (auth)
      {
        path: "",
        element: <AuthLayout />,
        children: [
          {
            path: "/register/",
            lazy: importComponent(() => import("./Register")),
          },
          {
            path: "/return/",
            lazy: importComponent(() => import("./Return")),
          },
          {
            path: "",
            element: <NavLayout />,
            errorElement: (
              <NotFoundPage>
                <SignOutButton />
              </NotFoundPage>
            ),
            children: [
              {
                path: "/self/",
                lazy: importComponent(() => import("./Self")),
                header: <HeaderBar title={s`Profile`} />,
              },
              {
                path: "/calendar/",
                lazy: importComponent(() => import("./Calendar")),
                header: <HeaderBar title={s`Calendar`} />,
                children: [
                  {
                    path: "",
                    lazy: importComponent(() => import("./CalendarHome")),
                  },
                ],
              },
              {
                path: "/calendar/:day/",
                lazy: importComponent(() => import("./Calendar")),
                header: <HeaderBar title={<Link href="/calendar">{s`Calendar`}</Link>} />,
                children: [
                  {
                    path: "",
                    lazy: importComponent(() => import("./CalendarHome")),
                  },
                  {
                    path: ":meetingId/",
                    lazy: importComponent(() => import("./Event")),
                    headerLazy: lazy(() => import("./EventHeader")),
                    children: [
                      {
                        path: "summary?/",
                        lazy: importComponent(() => import("./EventSummary")),
                      },
                      {
                        path: "working/",
                        action: passFormDataAction,
                        lazy: importComponent(() => import("./EventWorkspace")),
                        children: [
                          {
                            path: "file/",
                            element: <ModalLayout parent="/calendar/:day/:id/working" />,
                            children: [{ path: "", lazy: importComponent(() => import("./FilePreview")) }],
                          },
                        ],
                      },
                      {
                        path: "notes/",
                        action: passFormDataAction,
                        lazy: importComponent(() => import("./EventNotes")),
                      },
                      {
                        path: "transcript/",
                        lazy: importComponent(() => import("./EventTranscript")),
                        action: async ({ request }) => {
                          const formData = await request.formData()
                          const file = formData.get("file") as File
                          if (!file || !file.type.startsWith("audio/")) {
                            return { error: "Please upload a valid audio file." }
                          }
                          return clusterAPI.post("meetings/upload/manual", formData)
                        },
                      },
                    ],
                  },
                ],
              },
              {
                path: "/dashboard/",
                children: [
                  {
                    path: ":id/",
                    lazy: importComponent(() => import("./ThreadNav")),
                    headerLazy: lazy(() => import("./ProjectHeaderBar")),
                    children: [
                      { path: "", lazy: importComponent(() => import("./ProjectOverview")) },
                      {
                        path: "notes/:noteId/",
                        action: passFormDataAction,
                        lazy: importComponent(() => import("./ProjectNotes")),
                      },
                      {
                        path: "chat/:chatId/",
                        action: passFormDataAction,
                        lazy: importComponent(() => import("./ProjectChat")),
                        children: [
                          {
                            path: "file/",
                            element: <ModalLayout parent="/dashboard/:id/chat/:chatId" />,
                            children: [{ path: "", lazy: importComponent(() => import("./FilePreview")) }],
                          },
                        ],
                      },
                      {
                        path: "meeting/:meetingId/",
                        lazy: importComponent(() => import("./Event")),
                        children: [
                          {
                            path: "summary?/",
                            lazy: importComponent(() => import("./EventSummary")),
                          },
                          {
                            path: "working/",
                            action: passFormDataAction,
                            lazy: importComponent(() => import("./EventWorkspace")),
                            children: [
                              {
                                path: "*",
                                element: <ModalLayout parent="/calendar/:day/:id/working" />,
                                children: [{ path: "file/", lazy: importComponent(() => import("./FilePreview")) }],
                              },
                            ],
                          },
                          {
                            path: "notes/",
                            action: passFormDataAction,
                            lazy: importComponent(() => import("./EventNotes")),
                          },
                          {
                            path: "transcript/",
                            lazy: importComponent(() => import("./EventTranscript")),
                            action: async ({ request }) => {
                              const formData = await request.formData()
                              const file = formData.get("file") as File
                              if (!file || !file.type.startsWith("audio/")) {
                                return { error: "Please upload a valid audio file." }
                              }
                              return clusterAPI.post("meetings/upload/manual", formData)
                            },
                          },
                        ],
                      },
                      {
                        path: "context/",
                        children: [
                          {
                            path: "file/",
                            element: <ModalLayout parent="/dashboard/:id/" />,
                            children: [{ path: "", lazy: importComponent(() => import("./FilePreview")) }],
                          },
                        ],
                      },
                      {
                        path: "add-context/",
                        element: <ModalLayout parent="/dashboard/:id/" />,
                        children: [
                          {
                            path: "",
                            lazy: importComponent(() => import("./AddContext")),
                            children: [
                              {
                                path: ":pluginSlug",
                              },
                            ],
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
              {
                path: "/context/",
                // lazy: importComponent(() => import("./ContextLayout")),
                lazy: importComponent(() => import("./ContextGraphViewer")),
                header: <HeaderBar title={<Link href="/context">{s`Context`}</Link>} />,
                children: [
                  {
                    path: "questions/",
                    lazy: importComponent(() => import("./QNA")),
                  },
                  {
                    path: ":conceptId/",
                    lazy: importComponent(() => import("./ConceptViewer")),
                    children: [
                      {
                        path: "",
                        lazy: importComponent(() => import("./ConceptDetails")),
                      },
                      {
                        path: "history/",
                        lazy: importComponent(() => import("./ConceptHistory")),
                      },
                      {
                        path: "memories/",
                        lazy: importComponent(() => import("./ConceptMemories")),
                      },
                    ],
                  },
                ],
              },
              {
                path: "/settings/",
                element: (
                  <ModalLayout
                    className="relative flex flex-col overflow-auto rounded-3xl bg-white shadow-lg dark:bg-black"
                    style={{ maxHeight: "90dvh", minHeight: "40dvh" }}
                  />
                ),
                children: [
                  {
                    path: "",
                    lazy: importComponent(() => import("./Settings")),
                    children: [
                      {
                        path: "",
                        lazy: importComponent(() => import("./AccountSettings")),
                      },
                      {
                        path: "preferences/",
                        lazy: importComponent(() => import("./UserPreferences")),
                      },
                      {
                        path: "connectors/",
                        lazy: importComponent(() => import("./ConnectorSettings")),
                      },
                      {
                        path: "usage/",
                        lazy: importComponent(() => import("./UsageSettings")),
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
]

// export const headerRoutes = recurseHeaderRoutes(routes)
const router = createBrowserRouter(routes as RouteObject[])

export const RouteRenderer = () => {
  return <RouterProvider router={router} />
}
export default RouteRenderer

/**
 * Recursively flattens routes with headers into a format suitable for `useRoutes`.
 */
function flattenRoutes(routesWithHeader: RouteWithHeader[], parentPath: string): RouteObject[] {
  return routesWithHeader
    .flatMap((route) => {
      const { header, headerLazy: LazyHeader, path, children } = route
      const combinedPath = [parentPath, path].join("")

      const flattenedChildren = children ? flattenRoutes(children, combinedPath) : []
      if (header === undefined && LazyHeader === undefined && children) {
        return flattenedChildren
      }
      if (header === undefined && LazyHeader === undefined && !children) {
        return []
      }
      const newRoute: RouteObject = {
        path: [combinedPath, flattenedChildren.length < 1 ? "*" : ""].join(""),
        children: children ? flattenedChildren.filter(Boolean) : undefined,
      } as any
      if (LazyHeader) {
        newRoute.element = <LazyHeader />
      } else if (header) {
        newRoute.element = header
      }

      return newRoute
    })
    .filter(Boolean)
}

const headerRoutes = flattenRoutes(routes, "")

export const HeaderRenderer = () => {
  const header = useRoutes(headerRoutes)

  return header
}
